/**
 * A presented document, drawn as a file.
 *
 * Jonathon, 2026-09-02: "I need to be able to see the signed doc. best if it
 * saves it as a pdf." The document itself is a web page — it has to be, because
 * a web page is what can be signed and can record that somebody opened it — so
 * this is the other half: the same content as something that can be attached to
 * an email, handed to a lender, or kept.
 *
 * What is on it is decided in `packages/core/src/signedDocument.ts` and nowhere
 * else; this file only draws what that hands over. The split is the rule about
 * business logic living in core, and it is what lets the contents be tested
 * without a PDF engine.
 *
 * Drawn in the browser, on demand, for the reason the invoice is: nothing on
 * the server can make a PDF, and a rep opening a job on a driveway should not
 * download a PDF engine to look at it.
 *
 * No photographs. The findings gallery and the colour charts are files in a
 * bucket, and a record that quietly loses them when the network is slow is
 * worse than one that never claimed to carry them — the copy in Roof Board
 * still shows every photograph.
 */

import type { DocumentBlock } from "@roof-hawk/core";

import { COMPANY_LOGO_URL } from "../logo";

/** Letter, in points, which is what jsPDF measures in. */
const PAGE = { width: 612, height: 792 };
const MARGIN = 54;
const RIGHT = PAGE.width - MARGIN;
const WIDTH = RIGHT - MARGIN;
/** Where a page after the first starts, and the last line it may hold. */
const TOP = 72;
const BOTTOM = PAGE.height - 64;

const INK = "#111111";
const GREY = "#6b7280";
const RULE = "#d4d4d8";

/** How much of the line width a pair's label may take before it wraps. */
const LABEL_WIDTH = 210;
const VALUE_LEFT = MARGIN + LABEL_WIDTH + 12;

type Doc = import("jspdf").jsPDF;

/**
 * The company's mark, as bytes a PDF can hold.
 *
 * Fetched rather than imported for the reason the invoice fetches it: the
 * artwork changes when the company rebrands (CLAUDE.md rule 11) and a
 * build-time import would freeze a copy into the bundle. A mark that will not
 * load is a cosmetic loss, never a reason to withhold the record.
 */
async function logoDataUrl(url = COMPANY_LOGO_URL): Promise<string | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** A cursor down the page that starts a new one when it runs out of room. */
class Cursor {
  y = TOP;

  constructor(private readonly doc: Doc) {}

  /** Room for `height` points, on this page or the next one. */
  room(height: number): number {
    if (this.y + height > BOTTOM) {
      this.doc.addPage();
      this.y = TOP;
    }
    const at = this.y;
    this.y += height;
    return at;
  }
}

function text(
  doc: Doc,
  cursor: Cursor,
  body: string,
  {
    size,
    style = "normal",
    colour = INK,
    lead = 1.35,
    after = 6,
    x = MARGIN,
    width = WIDTH,
  }: {
    size: number;
    style?: "normal" | "bold";
    colour?: string;
    lead?: number;
    after?: number;
    x?: number;
    width?: number;
  },
): void {
  doc.setFont("helvetica", style);
  doc.setFontSize(size);
  doc.setTextColor(colour);
  const lines = doc.splitTextToSize(body, width) as string[];
  const step = size * lead;
  // Line by line, so a long paragraph breaks across a page instead of running
  // off the bottom of one. A contract is mostly long paragraphs.
  for (const line of lines) {
    const at = cursor.room(step);
    doc.text(line, x, at + size);
  }
  cursor.y += after;
}

/** One label-and-value row: an upgrade, a total, a line of the certificate. */
function pair(
  doc: Doc,
  cursor: Cursor,
  label: string,
  value: string,
  emphasis: boolean,
): void {
  doc.setFont("helvetica", emphasis ? "bold" : "normal");
  doc.setFontSize(emphasis ? 13 : 10.5);
  const size = emphasis ? 13 : 10.5;
  const labelLines = doc.splitTextToSize(label, LABEL_WIDTH) as string[];
  const valueLines = doc.splitTextToSize(value, RIGHT - VALUE_LEFT) as string[];
  const height = Math.max(labelLines.length, valueLines.length) * size * 1.35;
  const at = cursor.room(height + 8);

  doc.setTextColor(emphasis ? INK : GREY);
  doc.text(labelLines, MARGIN, at + size);
  doc.setTextColor(INK);
  doc.setFont("helvetica", emphasis ? "bold" : "normal");
  doc.text(valueLines, VALUE_LEFT, at + size);
}

function rule(doc: Doc, cursor: Cursor): void {
  const at = cursor.room(18);
  doc.setDrawColor(RULE);
  doc.setLineWidth(0.7);
  doc.line(MARGIN, at + 8, RIGHT, at + 8);
}

/**
 * Draw the whole thing.
 *
 * Exported for the test, which reads the words back out of the file rather than
 * looking at it.
 */
export async function renderDocumentRecord(
  blocks: readonly DocumentBlock[],
  options: { title?: string; logo?: string | null } = {},
): Promise<Doc> {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({
    unit: "pt",
    format: "letter",
    // Uncompressed, so a test can read the words back out and so anybody's
    // document scanner has nothing to fail at.
    compress: false,
  });

  doc.setProperties({
    title: options.title ?? "Document",
    subject: options.title ?? "Document",
  });

  const cursor = new Cursor(doc);
  const logo = options.logo === undefined ? await logoDataUrl() : options.logo;
  if (logo) {
    try {
      doc.addImage(logo, "PNG", RIGHT - 54, 40, 54, 54);
    } catch {
      // A mark that will not draw is not a reason to withhold the record.
    }
  }

  for (const block of blocks) {
    switch (block.kind) {
      case "title":
        // The title clears the mark in the corner rather than running under it.
        text(doc, cursor, block.text, {
          size: 22,
          style: "bold",
          after: 14,
          width: WIDTH - 70,
        });
        break;
      case "heading":
        cursor.y += 10;
        text(doc, cursor, block.text, { size: 15, style: "bold", after: 8 });
        break;
      case "subheading":
        text(doc, cursor, block.text, { size: 12, style: "bold", after: 4 });
        break;
      case "paragraph":
        text(doc, cursor, block.text, { size: 10.5, after: 8 });
        break;
      case "bullet":
        text(doc, cursor, `•  ${block.text}`, {
          size: 10.5,
          after: 2,
          x: MARGIN + 10,
          width: WIDTH - 10,
        });
        break;
      case "pair":
        pair(doc, cursor, block.label, block.value, false);
        break;
      case "total":
        pair(doc, cursor, block.label, block.value, true);
        break;
      case "rule":
        rule(doc, cursor);
        break;
    }
  }

  return doc;
}

/** Hand the file to whoever pressed the button. */
export async function downloadDocumentRecord(
  blocks: readonly DocumentBlock[],
  filename: string,
): Promise<void> {
  const doc = await renderDocumentRecord(blocks, { title: filename });
  doc.save(filename);
}
